import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { show as showIntercom } from '@intercom/messenger-js-sdk'
import { useAuthStore } from '../../stores/authStore.js'
import { useTenantStore } from '../../stores/tenantStore.js'
import { useLogout } from '../../hooks/useLogout.js'
import { useContactStrategy } from '../../hooks/useContactStrategy.js'
import { isDrayNowHost } from '../../lib/config/multitenancy.js'

// Route-aware search config (placeholder + label)
function getSearchConfig(pathname) {
  if (pathname.includes('/marketplace'))
    return {
      placeholder: "Search for reference #'s, companies...",
      label: { icon: 'search', basic: true, content: 'Order Search' },
    }
  if (pathname.startsWith('/lanes'))
    return {
      placeholder: 'Search for lanes (i.e. by name)',
      label: { icon: 'search', basic: true, content: 'Lane Search' },
    }
  if (pathname.startsWith('/admin/places'))
    return {
      placeholder: 'Search for places',
      label: { icon: 'search', content: 'Places Search' },
    }
  if (pathname.startsWith('/admin/users'))
    return {
      placeholder: 'Search for users',
      label: { icon: 'search', content: 'User Search' },
    }
  if (pathname.startsWith('/admin/companies'))
    return {
      placeholder: 'Search for companies',
      label: { icon: 'search', content: 'Companies Search' },
    }
  if (pathname.startsWith('/admin/translations'))
    return {
      placeholder: 'Search keys and Spanish text',
      label: { icon: 'search', content: 'Translation Search' },
    }
  return null
}

export function useNavBarLogic() {
  const [search, setSearch] = useState('')
  const [showHelp, setShowHelp] = useState(false)
  const [adminResource, setAdminResource] = useState(false)
  const [showSearch, setShowSearch] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const user = useAuthStore((s) => s.user)
  const privacyPolicyUrl = useTenantStore((s) => s.privacyPolicyUrl)
  const termsConditionsUrl = useTenantStore((s) => s.termsConditionsUrl)
  const contactStrategy = useContactStrategy()
  const logout = useLogout()

  const isAdmin = user?.role === 'admin'
  const isDrayNow = isDrayNowHost()

  // Route-aware search config
  const searchConfig = useMemo(
    () => getSearchConfig(location.pathname),
    [location.pathname],
  )

  // Sync search input from URL search params
  useEffect(() => {
    const { pathname } = location
    const isAdminPath = pathname.startsWith('/admin/')

    if (pathname.includes('/marketplace')) {
      const query = searchParams.get('query') || ''
      setSearch(query)
    } else if (pathname.includes('/lanes')) {
      const laneSearch = searchParams.get('search') || ''
      setSearch(laneSearch)
    } else if (isAdminPath) {
      const adminSearch = searchParams.get('search') || ''
      setSearch(adminSearch)
    } else {
      setSearch('')
    }

    setAdminResource(isAdminPath)
  }, [location.pathname, searchParams])

  // Determine whether to show search bar based on route
  useEffect(() => {
    const { pathname } = location

    if (pathname.includes('/marketplace')) {
      setShowSearch(true)
    } else if (pathname.startsWith('/admin/')) {
      const pathSegments = pathname
        .split('/')
        .filter((segment) => segment.length > 0)
      if (pathSegments.length > 2) {
        setShowSearch(false)
        return
      }
      setShowSearch(true)
    } else if (pathname.startsWith('/lanes')) {
      setShowSearch(true)
    } else {
      setShowSearch(false)
    }
  }, [location.pathname])

  // Handle search input change (local state only — URL updates on submit/blur)
  const handleSearchChange = useCallback((e, { value }) => {
    setSearch(value)
  }, [])

  const toggleHelpModal = useCallback(() => {
    setShowHelp((prev) => !prev)
  }, [])

  const handleHelpClick = useCallback(() => {
    if (contactStrategy === 'intercom') {
      showIntercom()
      return
    }
    if (contactStrategy === 'custom') {
      toggleHelpModal()
    }
  }, [contactStrategy, toggleHelpModal])

  const submitSearch = useCallback(() => {
    const trimmed = search.trim()
    const { pathname } = location

    if (pathname.includes('/marketplace')) {
      const segments = pathname.split('/').filter(Boolean)
      const basePath = `/marketplace/${segments[1] || 'all'}/${segments[2] || 'open'}`
      const newParams = new URLSearchParams(searchParams)
      if (trimmed) newParams.set('query', trimmed)
      else newParams.delete('query')
      const qs = newParams.toString()
      navigate(`${basePath}${qs ? `?${qs}` : ''}`)
    } else if (pathname.includes('/lanes')) {
      const newParams = new URLSearchParams(searchParams)
      if (trimmed) newParams.set('search', trimmed)
      else newParams.delete('search')
      setSearchParams(newParams)
    } else if (pathname.startsWith('/admin/')) {
      const newParams = new URLSearchParams(searchParams)
      if (trimmed) {
        newParams.set('search', trimmed)
        newParams.set('page', '0')
      } else {
        newParams.delete('search')
        newParams.set('page', '0')
      }
      setSearchParams(newParams)
    }
  }, [search, location, searchParams, setSearchParams, navigate])

  const handleSubmitSearch = useCallback(
    (e) => {
      e.preventDefault()
      submitSearch()
    },
    [submitSearch],
  )

  const handleSearchBlur = useCallback(() => {
    const trimmed = search.trim()
    const { pathname } = location

    let currentUrlValue = ''
    if (pathname.includes('/marketplace')) {
      currentUrlValue = searchParams.get('query') || ''
    } else if (pathname.includes('/lanes') || pathname.startsWith('/admin/')) {
      currentUrlValue = searchParams.get('search') || ''
    }

    if (trimmed !== currentUrlValue) {
      submitSearch()
    }
  }, [search, location, searchParams, submitSearch])

  // Clear search for all route types
  const clearSearch = useCallback(() => {
    const { pathname } = location

    setSearch('')

    if (pathname.includes('/marketplace')) {
      const segments = pathname.split('/').filter(Boolean)
      const basePath = `/marketplace/${segments[1] || 'all'}/${segments[2] || 'open'}`
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('query')
      const qs = newParams.toString()
      navigate(`${basePath}${qs ? `?${qs}` : ''}`)
    } else if (pathname.includes('/lanes')) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('search')
      setSearchParams(newParams)
    } else if (pathname.startsWith('/admin/')) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('search')
      newParams.set('page', '0')
      setSearchParams(newParams)
    }
  }, [location.pathname, searchParams, setSearchParams, navigate])

  const hasActiveSearch = search.length > 0

  // Hide the Help dropdown item until tenant config has resolved (strategy
  // 'pending') and when neither Intercom nor the custom modal applies
  // ('none'). Otherwise render it; click behavior is decided by handleHelpClick.
  const showContactSupport =
    contactStrategy === 'intercom' || contactStrategy === 'custom'

  return {
    search,
    showHelp,
    showSearch,
    adminResource,
    isAdmin,
    isDrayNow,
    hasActiveSearch,
    user,
    privacyPolicyUrl,
    termsConditionsUrl,
    showContactSupport,
    contactStrategy,
    location,
    searchConfig,
    handleSearchChange,
    toggleHelpModal,
    handleHelpClick,
    handleSubmitSearch,
    handleSearchBlur,
    clearSearch,
    logout,
  }
}
